import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { toast } from "sonner";
import { Plus, Trash2, Send, CheckCircle2, AlertTriangle, Clock, Package2, ArrowLeft } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useConfirm } from "../components/ConfirmProvider";

export default function LoanedEquipment() {
  const confirm = useConfirm();
  const { canDo } = useAuth();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [persons, setPersons] = useState([]);
  const [projects, setProjects] = useState([]);
  const [filter, setFilter] = useState("all"); // all | loaned | overdue | returned
  const [dialog, setDialog] = useState(null); // {mode: 'add'|'edit'|'return', item}

  const load = async () => {
    const [it, ps, pr] = await Promise.all([
      api.get("/loaned-equipment"),
      api.get("/persons"),
      api.get("/projects").catch(() => ({ data: [] })),
    ]);
    setItems(it.data || []);
    setPersons((ps.data || []).filter((p) => p.active !== false));
    setProjects(pr.data || []);
  };
  useEffect(() => { load(); }, []);

  const filtered = items.filter((i) => filter === "all" || i.status === filter);
  const today = new Date().toISOString().slice(0, 10);

  const stats = {
    loaned: items.filter((i) => i.status === "loaned").length,
    overdue: items.filter((i) => i.status === "overdue").length,
    returned: items.filter((i) => i.status === "returned").length,
  };

  return (
    <div data-testid="loaned-equipment-page">
      <button onClick={() => navigate("/inventar")} className="text-xs text-zinc-500 hover:text-zinc-900 mb-4 inline-flex items-center gap-1">
        <ArrowLeft size={14}/> Înapoi la Inventar
      </button>
      <div className="flex items-end justify-between mb-6">
        <div>
          <h1 className="font-display text-3xl font-extrabold tracking-tight flex items-center gap-3">
            <Package2 size={28} className="text-[var(--primary)]"/>
            Echipamente împrumutate
          </h1>
          <p className="text-sm text-zinc-500 mt-1">Urmărește ce s-a împrumutat, cui și până când. Reminder automat în ziua returnării.</p>
        </div>
        {canDo("INVENTAR", "edit") && (
          <Button onClick={() => setDialog({ mode: "add" })} className="bg-[var(--primary)]" data-testid="add-loaned-btn">
            <Plus size={16} className="mr-1"/> Împrumută echipament
          </Button>
        )}
      </div>

      {/* Stats + filtre */}
      <div className="grid grid-cols-3 gap-3 mb-6">
        <button onClick={() => setFilter(filter === "loaned" ? "all" : "loaned")}
          className={`card-flat p-4 text-left transition-colors ${filter === "loaned" ? "border-[var(--primary)] bg-[var(--primary-subtle)]" : "hover:border-[var(--primary)]"}`}
          data-testid="filter-loaned">
          <div className="flex items-center gap-2 small-label"><Clock size={14}/> În curs</div>
          <div className="font-display text-3xl font-extrabold text-zinc-900 mt-1">{stats.loaned}</div>
        </button>
        <button onClick={() => setFilter(filter === "overdue" ? "all" : "overdue")}
          className={`card-flat p-4 text-left transition-colors ${filter === "overdue" ? "border-red-500 bg-red-50" : "hover:border-red-400"}`}
          data-testid="filter-overdue">
          <div className="flex items-center gap-2 small-label text-red-600"><AlertTriangle size={14}/> Întârziate</div>
          <div className="font-display text-3xl font-extrabold text-red-600 mt-1">{stats.overdue}</div>
        </button>
        <button onClick={() => setFilter(filter === "returned" ? "all" : "returned")}
          className={`card-flat p-4 text-left transition-colors ${filter === "returned" ? "border-green-500 bg-green-50" : "hover:border-green-400"}`}
          data-testid="filter-returned">
          <div className="flex items-center gap-2 small-label text-green-600"><CheckCircle2 size={14}/> Returnate</div>
          <div className="font-display text-3xl font-extrabold text-green-600 mt-1">{stats.returned}</div>
        </button>
      </div>

      {/* Lista */}
      <div className="card-flat overflow-hidden">
        {filtered.length === 0 ? (
          <div className="text-center text-sm text-zinc-400 italic py-12">
            {filter === "all" ? "Niciun echipament împrumutat." : `Niciun echipament cu statusul "${filter}".`}
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-zinc-50 text-[10px] uppercase tracking-wider font-bold text-zinc-500">
              <tr>
                <th className="text-left p-3">Echipament</th>
                <th className="text-left p-3">Împrumutat la</th>
                <th className="text-left p-3">Data împrumut</th>
                <th className="text-left p-3">Returnare</th>
                <th className="text-left p-3">Status</th>
                <th className="text-right p-3">Acțiuni</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((it) => {
                const isOverdue = it.status === "overdue" || (it.status === "loaned" && it.expected_return < today);
                const daysToReturn = it.status === "loaned" ? Math.ceil((new Date(it.expected_return) - new Date(today)) / (24 * 3600 * 1000)) : null;
                const reminderToday = daysToReturn === 0;
                return (
                  <tr key={it.id} className="border-t border-[var(--border)] hover:bg-zinc-50/60" data-testid={`loaned-row-${it.id}`}>
                    <td className="p-3">
                      <div className="font-semibold">{it.item_name}</div>
                      {it.serial_number && <div className="text-[10px] text-zinc-400">SN: {it.serial_number}</div>}
                      {it.quantity > 1 && <div className="text-[10px] text-zinc-500">Cantitate: {it.quantity}</div>}
                    </td>
                    <td className="p-3">
                      {it.borrower_type === "person" ? (
                        <span className="inline-flex items-center gap-1.5">
                          <span className="w-1.5 h-1.5 rounded-full bg-blue-500"/>
                          {it.borrower_name || "—"}
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1.5">
                          <span className="w-1.5 h-1.5 rounded-full bg-purple-500"/>
                          {it.borrower_company}
                          {it.borrower_contact && <span className="text-[10px] text-zinc-400">· {it.borrower_contact}</span>}
                        </span>
                      )}
                    </td>
                    <td className="p-3 text-zinc-600">{it.loaned_at}</td>
                    <td className="p-3">
                      <div>
                        <span className={isOverdue ? "text-red-600 font-bold" : reminderToday ? "text-amber-600 font-bold" : "text-zinc-700"}>
                          {it.expected_return}
                        </span>
                        {reminderToday && <span className="ml-1 text-[10px] bg-amber-100 text-amber-800 px-1.5 py-0.5 rounded font-bold">⏰ AZI!</span>}
                        {it.actual_return && <div className="text-[10px] text-green-600">Returnat: {it.actual_return}</div>}
                        {it.return_storage_location && <div className="text-[10px] text-zinc-500">📦 {it.return_storage_location}</div>}
                      </div>
                    </td>
                    <td className="p-3">
                      <StatusBadge status={it.status}/>
                    </td>
                    <td className="p-3 text-right">
                      {(it.status === "loaned" || it.status === "overdue") && canDo("INVENTAR", "edit") && (
                        <Button size="sm" variant="outline" onClick={() => setDialog({ mode: "return", item: it })} className="mr-1" data-testid={`return-btn-${it.id}`}>
                          <Send size={12} className="mr-1"/>Returnează
                        </Button>
                      )}
                      {canDo("INVENTAR", "delete") && (
                        <button
                          onClick={async () => {
                            if ((await confirm({ body: `Ștergi echipamentul "${it.item_name}"?`, danger: true }))) {
                              await api.delete(`/loaned-equipment/${it.id}`);
                              toast.success("Șters"); load();
                            }
                          }}
                          className="p-1.5 hover:bg-red-50 rounded text-red-600"
                          title="Șterge"
                          data-testid={`del-btn-${it.id}`}
                        ><Trash2 size={14}/></button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {dialog?.mode === "add" && (
        <LoanDialog persons={persons} projects={projects} onClose={() => setDialog(null)} onSaved={() => { setDialog(null); load(); }}/>
      )}
      {dialog?.mode === "return" && (
        <ReturnDialog item={dialog.item} onClose={() => setDialog(null)} onSaved={() => { setDialog(null); load(); }}/>
      )}
    </div>
  );
}

function StatusBadge({ status }) {
  const map = {
    loaned: { label: "În curs", cls: "bg-blue-100 text-blue-800" },
    overdue: { label: "Întârziat", cls: "bg-red-100 text-red-800" },
    returned: { label: "Returnat", cls: "bg-green-100 text-green-800" },
  };
  const v = map[status] || { label: status || "—", cls: "bg-zinc-100 text-zinc-700" };
  return <span className={`text-[11px] px-2 py-0.5 rounded font-bold ${v.cls}`} data-testid={`status-${status}`}>{v.label}</span>;
}

function LoanDialog({ persons, projects, onClose, onSaved }) {
  const [form, setForm] = useState({
    item_name: "",
    serial_number: "",
    quantity: 1,
    borrower_type: "person",
    borrower_person_id: "",
    borrower_company: "",
    borrower_contact: "",
    project_id: "",
    loaned_at: new Date().toISOString().slice(0, 10),
    expected_return: "",
    note: "",
  });

  const submit = async () => {
    if (!form.item_name.trim()) return toast.error("Denumire echipament obligatorie");
    if (!form.expected_return) return toast.error("Data returnare obligatorie");
    if (form.borrower_type === "person" && !form.borrower_person_id) return toast.error("Selectează persoana");
    if (form.borrower_type === "company" && !form.borrower_company.trim()) return toast.error("Specifică firma");
    try {
      await api.post("/loaned-equipment", {
        ...form,
        quantity: parseFloat(form.quantity) || 1,
        borrower_person_id: form.borrower_type === "person" ? form.borrower_person_id : null,
        borrower_company: form.borrower_type === "company" ? form.borrower_company.trim() : null,
        project_id: form.project_id || null,
      });
      toast.success("Împrumut înregistrat");
      onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || "Eroare"); }
  };

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="loan-dialog">
        <DialogHeader><DialogTitle>Împrumută echipament</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="small-label">Denumire echipament</Label>
            <Input value={form.item_name} onChange={(e) => setForm({ ...form, item_name: e.target.value })} placeholder="ex: Cabină Schleuse #3" data-testid="loan-item-name"/>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="small-label">Serie (opțional)</Label>
              <Input value={form.serial_number} onChange={(e) => setForm({ ...form, serial_number: e.target.value })} data-testid="loan-serial"/>
            </div>
            <div>
              <Label className="small-label">Cantitate</Label>
              <Input type="number" min={1} step="0.5" value={form.quantity} onChange={(e) => setForm({ ...form, quantity: e.target.value })}/>
            </div>
          </div>
          <div>
            <Label className="small-label">Tip împrumutator</Label>
            <div className="flex gap-2 mt-1.5">
              <button type="button"
                onClick={() => setForm({ ...form, borrower_type: "person" })}
                className={`flex-1 py-2 px-3 rounded border text-sm font-semibold ${form.borrower_type === "person" ? "bg-[var(--primary)] text-white border-[var(--primary)]" : "bg-white border-zinc-200"}`}
                data-testid="loan-type-person">👤 Persoană internă</button>
              <button type="button"
                onClick={() => setForm({ ...form, borrower_type: "company" })}
                className={`flex-1 py-2 px-3 rounded border text-sm font-semibold ${form.borrower_type === "company" ? "bg-purple-600 text-white border-purple-600" : "bg-white border-zinc-200"}`}
                data-testid="loan-type-company">🏢 Firmă externă</button>
            </div>
          </div>
          {form.borrower_type === "person" ? (
            <div>
              <Label className="small-label">Persoana</Label>
              <Select value={form.borrower_person_id} onValueChange={(v) => setForm({ ...form, borrower_person_id: v })}>
                <SelectTrigger data-testid="loan-person"><SelectValue placeholder="Selectează"/></SelectTrigger>
                <SelectContent>
                  {persons.map((p) => <SelectItem key={p.id} value={p.id}>{p.nume} {p.prenume}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          ) : (
            <>
              <div>
                <Label className="small-label">Nume firmă</Label>
                <Input value={form.borrower_company} onChange={(e) => setForm({ ...form, borrower_company: e.target.value })} placeholder="ex: ABC Construcții GmbH" data-testid="loan-company"/>
              </div>
              <div>
                <Label className="small-label">Contact (telefon / email)</Label>
                <Input value={form.borrower_contact} onChange={(e) => setForm({ ...form, borrower_contact: e.target.value })}/>
              </div>
            </>
          )}
          <div>
            <Label className="small-label">Proiect asociat (opțional)</Label>
            <Select value={form.project_id || "__none__"} onValueChange={(v) => setForm({ ...form, project_id: v === "__none__" ? "" : v })}>
              <SelectTrigger><SelectValue placeholder="—"/></SelectTrigger>
              <SelectContent>
                <SelectItem value="__none__">— Niciunul —</SelectItem>
                {projects.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="small-label">Data împrumut</Label>
              <Input type="date" value={form.loaned_at} onChange={(e) => setForm({ ...form, loaned_at: e.target.value })}/>
            </div>
            <div>
              <Label className="small-label">Data returnare așteptată</Label>
              <Input type="date" value={form.expected_return} onChange={(e) => setForm({ ...form, expected_return: e.target.value })} data-testid="loan-expected-return"/>
            </div>
          </div>
          <div>
            <Label className="small-label">Notă (opțional)</Label>
            <Textarea rows={2} value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })}/>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Anulează</Button>
          <Button onClick={submit} className="bg-[var(--primary)]" data-testid="loan-submit">Salvează</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ReturnDialog({ item, onClose, onSaved }) {
  const [storage, setStorage] = useState("");
  const [actual, setActual] = useState(new Date().toISOString().slice(0, 10));
  const submit = async () => {
    try {
      await api.post(`/loaned-equipment/${item.id}/return`, {
        return_storage_location: storage,
        actual_return: actual,
      });
      toast.success("Marcat ca returnat");
      onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || "Eroare"); }
  };
  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-md" data-testid="return-dialog">
        <DialogHeader><DialogTitle>Returnează „{item.item_name}"</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="small-label">Data returnare</Label>
            <Input type="date" value={actual} onChange={(e) => setActual(e.target.value)} data-testid="return-actual-date"/>
          </div>
          <div>
            <Label className="small-label">Unde a fost depozitat?</Label>
            <Input
              value={storage}
              onChange={(e) => setStorage(e.target.value)}
              placeholder="ex: Magazia 1, raft 2"
              data-testid="return-storage"
            />
            <p className="text-[10px] text-zinc-500 mt-1">Specifică locația concretă (magazia, raftul, etc.) pentru a regăsi rapid echipamentul.</p>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Anulează</Button>
          <Button onClick={submit} className="bg-green-600 hover:bg-green-700" data-testid="return-submit">
            <CheckCircle2 size={14} className="mr-1"/>Confirmă returnare
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
